// Carriers Filter + All-carriers SectionCards (D3f + D3h + D3j).
// Mirror of LogisticsRoutesList for the routes lane: filter on top, list
// below. Per-row supports inline edit (Plants-style: editingId + edit
// draft on the panel hook), a toggle-active button, plus two sibling
// sub-card toggles:
//   * "Routes" -> LogisticsCarriersRouteHistoryCard (D3h, read-only)
//   * "Rigs"   -> LogisticsCarriersFleetCard         (D3j, CRUD)
// Single-open: opening one sub-card closes the other so the panel stays
// scrollable. Editing a carrier auto-closes both sub-cards (the inline
// edit form is enough vertical real estate on its own).
//
// File-size hygiene: the per-row display + edit components were extracted
// to LogisticsCarriersDisplayRow.tsx and LogisticsCarriersEditRow.tsx in
// the D3j prep split so this orchestrator stays compact.
import { Fragment, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import {
  CARRIER_KIND_LABELS,
  CARRIER_KINDS,
  inputClass,
  type CarriersActiveFilter,
} from "./logisticsCarriersShared";
import {
  LogisticsCarriersDisplayRow,
  type CarrierRowExpansion,
} from "./LogisticsCarriersDisplayRow";
import { LogisticsCarriersEditRow } from "./LogisticsCarriersEditRow";
import { LogisticsCarriersFleetCard } from "./LogisticsCarriersFleetCard";
import { LogisticsCarriersRouteHistoryCard } from "./LogisticsCarriersRouteHistoryCard";
import { useLogisticsCarrierFleet } from "./useLogisticsCarrierFleet";
import { useLogisticsCarrierRouteHistory } from "./useLogisticsCarrierRouteHistory";
import type { useLogisticsCarriersPanel } from "./useLogisticsCarriersPanel";
import type { LogiCarrierKind } from "@/lib/logistics";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

type Props = {
  m: ReturnType<typeof useLogisticsCarriersPanel>;
};

export function LogisticsCarriersList({ m }: Props) {
  // Single-open expansion state. Each row can show at most one sub-card
  // (routes OR rigs) at a time, and only one row across the whole list
  // can have any sub-card open at all -- track the (carrierId, kind) pair
  // as a single piece of state.
  const [expanded, setExpanded] = useState<{
    carrierId: string;
    kind: CarrierRowExpansion;
  } | null>(null);
  // Editing a row auto-closes its sub-cards so the inline edit form
  // doesn't compete with them for vertical space.
  const effective = m.editingId ? null : expanded;
  const historyOpenId = effective?.kind === "routes" ? effective.carrierId : null;
  const fleetOpenId = effective?.kind === "rigs" ? effective.carrierId : null;
  const history = useLogisticsCarrierRouteHistory(historyOpenId);
  const fleet = useLogisticsCarrierFleet(fleetOpenId);

  const onToggle = (carrierId: string) => (kind: CarrierRowExpansion) => {
    setExpanded((prev) => {
      if (prev && prev.carrierId === carrierId && prev.kind === kind) return null;
      return { carrierId, kind };
    });
  };

  return (
    <>
      <SectionCard title="Filter">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Kind
            <select
              className={`${inputClass} mt-1`}
              value={m.filter.carrier_kind}
              onChange={(e) =>
                m.setFilter({
                  ...m.filter,
                  carrier_kind: (e.target.value as "" | LogiCarrierKind) || "",
                })
              }
            >
              <option value="">All kinds</option>
              {CARRIER_KINDS.map((k) => (
                <option key={k} value={k}>
                  {CARRIER_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Active
            <select
              className={`${inputClass} mt-1`}
              value={m.filter.is_active}
              onChange={(e) =>
                m.setFilter({
                  ...m.filter,
                  is_active: (e.target.value as CarriersActiveFilter) || "",
                })
              }
            >
              <option value="">All</option>
              <option value="true">Active only</option>
              <option value="false">Inactive only</option>
            </select>
          </label>
        </div>
      </SectionCard>

      <div {...{ [FIREFLY_TARGET_ATTR]: "logi-carriers-list" }}>
      <SectionCard title="All carriers">
        {m.listBusy ? (
          <div className="text-sm text-gray-500 dark:text-zinc-400">Loading carriers…</div>
        ) : null}
        {!m.listBusy && m.carriers.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-zinc-400">
            No carriers match the current filter. Use + New carrier above to add one, or
            change the filter.
          </div>
        ) : null}
        <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
          {m.carriers.map((c) => {
            const rowExpansion: CarrierRowExpansion | null =
              effective?.carrierId === c.id ? effective.kind : null;
            return (
              <Fragment key={c.id}>
                {m.editingId === c.id ? (
                  <LogisticsCarriersEditRow carrier={c} m={m} />
                ) : (
                  <LogisticsCarriersDisplayRow
                    carrier={c}
                    m={m}
                    expanded={rowExpansion}
                    onToggle={onToggle(c.id)}
                  />
                )}
                {historyOpenId === c.id ? (
                  <li className="py-2">
                    <LogisticsCarriersRouteHistoryCard
                      carrier={c}
                      h={history}
                      onClose={() => setExpanded(null)}
                    />
                  </li>
                ) : null}
                {fleetOpenId === c.id ? (
                  <li className="py-2">
                    <LogisticsCarriersFleetCard
                      carrier={c}
                      f={fleet}
                      onClose={() => setExpanded(null)}
                    />
                  </li>
                ) : null}
              </Fragment>
            );
          })}
        </ul>
      </SectionCard>
      </div>
    </>
  );
}
